import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import styles from './CycleTimeBanner.module.scss';
import { DARK_MODE } from '../../constants';

const formatDuration = (totalSeconds) => {
	const abs = Math.abs(Math.floor(totalSeconds));
	const h = Math.floor(abs / 3600);
	const m = Math.floor((abs % 3600) / 60);
	const s = abs % 60;
	const pad = (n) => String(n).padStart(2, '0');
	if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
	return `${pad(m)}:${pad(s)}`;
};

const CycleTimeBanner = ({ cycleTime, receiveTs, themeMode }) => {
	const [elapsed, setElapsed] = useState(0);
	const intervalRef = useRef(null);

	useEffect(() => {
		if (!receiveTs) {
			setElapsed(0);
			return;
		}

		const tick = () => {
			const start = new Date(receiveTs).getTime();
			const now = Date.now();
			setElapsed(Math.floor((now - start) / 1000));
		};

		tick();
		intervalRef.current = setInterval(tick, 1000);
		return () => clearInterval(intervalRef.current);
	}, [receiveTs]);

	// Nothing to show if no part has been received yet
	if (!receiveTs) return null;

	const isDark = themeMode === DARK_MODE;

	// No cycle time configured (0 or missing)
	if (!cycleTime || cycleTime === 0) {
		return (
			<Box className={`${styles.banner} ${styles.bannerNeutral} ${isDark ? styles.bannerNeutralDark : styles.bannerNeutralLight}`}>
				<TimerOffIcon className={styles.icon} fontSize="small" />
				<Typography className={styles.label}>Cycle Time</Typography>
				<Typography className={styles.divider}>|</Typography>
				<Typography className={styles.statusText}>Not Configured</Typography>
				<Typography className={styles.divider}>|</Typography>
				<Typography className={styles.elapsed}>Elapsed: {formatDuration(elapsed)}</Typography>
			</Box>
		);
	}

	const remaining = cycleTime - elapsed;
	const isOvertime = remaining < 0;
	const progress = Math.min((elapsed / cycleTime) * 100, 100);

	return (
		<Box
			className={`${styles.banner} ${
				isOvertime
					? isDark ? styles.bannerOvertimeDark : styles.bannerOvertimeLight
					: isDark ? styles.bannerOkDark : styles.bannerOkLight
			}`}
		>
			{/* Progress track */}
			<Box className={styles.progressTrack}>
				<Box
					className={`${styles.progressFill} ${isOvertime ? styles.progressFillOvertime : styles.progressFillOk}`}
					style={{ width: `${progress}%` }}
				/>
			</Box>

			{/* Content row */}
			<Box className={styles.content}>
				{isOvertime ? (
					<WarningAmberIcon className={`${styles.icon} ${styles.iconOvertime}`} fontSize="small" />
				) : (
					<CheckCircleOutlineIcon className={`${styles.icon} ${styles.iconOk}`} fontSize="small" />
				)}

				<Typography className={styles.label}>Cycle Time</Typography>
				<Typography className={`${styles.totalTime} ${isOvertime ? styles.textOvertime : styles.textOk}`}>
					{formatDuration(cycleTime)}
				</Typography>

				<Typography className={styles.divider}>|</Typography>

				<Typography className={styles.label}>
					{isOvertime ? 'Overtime' : 'Remaining'}
				</Typography>
				<Typography
					className={`${styles.timeValue} ${isOvertime ? styles.textOvertime : styles.textOk}`}
				>
					{isOvertime ? `+${formatDuration(Math.abs(remaining))}` : formatDuration(remaining)}
				</Typography>

				<Typography className={styles.divider}>|</Typography>

				<Typography className={styles.label}>Elapsed</Typography>
				<Typography className={`${styles.timeValue} ${isOvertime ? styles.textOvertime : styles.textOk}`}>
					{formatDuration(elapsed)}
				</Typography>

				{isOvertime && (
					<Box className={styles.overtimeBadge}>
						<WarningAmberIcon fontSize="inherit" />
						EXCEEDED
					</Box>
				)}
			</Box>
		</Box>
	);
};

const mapStateToProps = (state) => ({
	cycleTime: state.user.cycleTime,
	receiveTs: state.station.receiveTs,
	themeMode: state.theme.themeMode,
});

export default connect(mapStateToProps)(CycleTimeBanner);
